import { useState, useEffect } from 'react';
import { X, Search, Link2, Unlink, Loader2, CheckCircle2, Target, DollarSign } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Modal for managing opportunities linked to a goal
 * Allows searching, linking, and unlinking opportunities
 */
const GoalOpportunityManagerModal = ({ goal, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('linked'); // 'linked' or 'available'
  const [linkedOpportunities, setLinkedOpportunities] = useState([]);
  const [availableOpportunities, setAvailableOpportunities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [linkingId, setLinkingId] = useState(null);
  const [unlinkingId, setUnlinkingId] = useState(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && goal) {
      loadLinkedOpportunities();
      loadAvailableOpportunities();
    }
  }, [isOpen, goal?.id]);

  // Reload available opportunities when search/filter changes
  useEffect(() => {
    if (isOpen && goal && activeTab === 'available') {
      const debounce = setTimeout(() => {
        loadAvailableOpportunities();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchQuery, stageFilter]);

  const loadLinkedOpportunities = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/goals/${goal.id}/opportunities`);
      setLinkedOpportunities(response.data.data.opportunities || []);
      setSummary(response.data.data.summary || null);
    } catch (error) {
      console.error('Error loading linked opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableOpportunities = async () => {
    try {
      const params = { limit: 50 };
      if (searchQuery) params.search = searchQuery;
      if (stageFilter) params.stage = stageFilter;
      
      const response = await apiClient.get(`/api/v1/goals/${goal.id}/available-opportunities`, { params });
      setAvailableOpportunities(response.data.data || []);
    } catch (error) {
      console.error('Error loading available opportunities:', error);
    }
  };

  const handleLinkOpportunity = async (opportunityId) => {
    try {
      setLinkingId(opportunityId);
      await apiClient.post(`/api/v1/goals/${goal.id}/opportunities`, null, {
        params: { opportunity_id: opportunityId }
      });
      
      // Reload both lists
      await Promise.all([loadLinkedOpportunities(), loadAvailableOpportunities()]);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error linking opportunity:', error);
      alert('Failed to link opportunity: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLinkingId(null);
    }
  };

  const handleUnlinkOpportunity = async (opportunityId) => {
    if (!window.confirm('Are you sure you want to unlink this opportunity?')) return;
    
    try {
      setUnlinkingId(opportunityId);
      await apiClient.delete(`/api/v1/goals/${goal.id}/opportunities/${opportunityId}`);
      
      // Reload both lists
      await Promise.all([loadLinkedOpportunities(), loadAvailableOpportunities()]);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error unlinking opportunity:', error);
      alert('Failed to unlink opportunity: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUnlinkingId(null);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatShortCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const getStageColor = (stage) => {
    const colors = {
      target: 'bg-gray-100 text-gray-800',
      qualified: 'bg-blue-100 text-blue-800',
      rfq: 'bg-yellow-100 text-yellow-800',
      quoted: 'bg-orange-100 text-orange-800',
      awarded: 'bg-green-100 text-green-800',
      sourced: 'bg-purple-100 text-purple-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen || !goal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Linked Opportunities</h2>
            <p className="text-sm text-gray-600 mt-1">{goal.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Summary Card */}
        {summary && (
          <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{summary.linked_count}</div>
                <div className="text-xs text-gray-600">Opportunities</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatShortCurrency(summary.total_weighted_revenue_cents)}
                </div>
                <div className="text-xs text-gray-600">Weighted Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700">
                  {formatShortCurrency(goal.target_revenue_cents)}
                </div>
                <div className="text-xs text-gray-600">Target</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {summary.revenue_progress_percent}%
                </div>
                <div className="text-xs text-gray-600">Progress</div>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(summary.revenue_progress_percent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('linked')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'linked'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Linked ({linkedOpportunities.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('available')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === 'available'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Available to Link
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'linked' ? (
            /* Linked Opportunities Tab */
            <div>
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : linkedOpportunities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No opportunities linked yet.</p>
                  <p className="text-sm mt-1">Switch to "Available to Link" tab to add opportunities.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {linkedOpportunities.map((opp) => (
                    <div
                      key={opp.id}
                      className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">{opp.program_name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStageColor(opp.stage)}`}>
                            {opp.stage}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {opp.oem} | {opp.confidence_percent || 0}% confidence
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-500">
                            Full: {formatCurrency(opp.revenue_potential_cents)}
                          </span>
                          <span className="text-green-600 font-medium">
                            Weighted: {formatCurrency(opp.weighted_revenue_cents)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnlinkOpportunity(opp.id)}
                        disabled={unlinkingId === opp.id}
                        className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                        title="Unlink opportunity"
                      >
                        {unlinkingId === opp.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Unlink className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Available Opportunities Tab */
            <div>
              {/* Search and Filter */}
              <div className="flex gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by program name, OEM, or platform..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Stages</option>
                  <option value="target">Target</option>
                  <option value="qualified">Qualified</option>
                  <option value="rfq">RFQ</option>
                  <option value="quoted">Quoted</option>
                  <option value="awarded">Awarded</option>
                </select>
              </div>

              {/* Available Opportunities List */}
              {availableOpportunities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No opportunities found.</p>
                  <p className="text-sm mt-1">Try adjusting your search or filters.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableOpportunities.map((opp) => (
                    <div
                      key={opp.id}
                      className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">{opp.program_name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStageColor(opp.stage)}`}>
                            {opp.stage}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {opp.oem} {opp.platform_code && `| ${opp.platform_code}`} | {opp.confidence_percent || 0}% confidence
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-500">
                            Full: {formatCurrency(opp.revenue_potential_cents)}
                          </span>
                          <span className="text-green-600 font-medium">
                            Weighted: {formatCurrency(opp.weighted_revenue_cents)}
                          </span>
                          {opp.sop_date && (
                            <span className="text-gray-400">
                              SOP: {new Date(opp.sop_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleLinkOpportunity(opp.id)}
                        disabled={linkingId === opp.id}
                        className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        {linkingId === opp.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Link2 className="w-4 h-4" />
                            Link
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoalOpportunityManagerModal;
